import { Auth0Client } from "@auth0/auth0-spa-js";
import { auth0Config } from "../../config";
import { CatalogAddon, CatalogResponse } from "../../types/catalog";
import SubscriptionClient from "./SubscriptionClient";
import CatalogClient from "./CatalogClient";
import InvoiceClient from "./InvoiceClient";
import ReceiptClient from "./ReceiptClient";
import MemberBillingClient from "./MemberBillingClient";
import PaymentMethodClient from "./PaymentMethodClient";
import BillingClient, {
  ApplyLateFeesResponse,
  BillingLedgerEntry,
  GenerateRecurringInvoicesResponse,
  ProcessDueInvoicesResponse,
  CheckTrialExpirationsResponse,
  ProcessPeriodEndJobResponse,
  SimulateDelinquencyRequest,
  SimulateDimepayWebhookRequest,
} from "./BillingClient";
import { Subscription } from "../../pages/facility/types/Subscription";
import { SeatChangeImpactResponseDto } from "../../types/subscription";
import { PaymentMethodResponseDto } from "../../types/payment-method";
import {
  BillingLedgerQueryOptions,
  SubscriptionInvoice,
  SubscriptionReceipt,
  UpdatePhysiciansResponse,
} from "../../types/billing";

import { EntitlementsApi, Entitlement } from "../entitlementsApi";

class SubscriptionApi {
  private auth0Client: Auth0Client;

  private subscriptionClient: SubscriptionClient;

  private catalogClient: CatalogClient;

  private invoiceClient: InvoiceClient;

  private receiptClient: ReceiptClient;

  private memberBillingClient: MemberBillingClient;

  private paymentMethodClient: PaymentMethodClient;

  private billingClient: BillingClient;

  private entitlementsApi: EntitlementsApi;

  constructor() {
    this.auth0Client = new Auth0Client({
      redirect_uri: window.location.origin,
      ...auth0Config,
    });
    this.subscriptionClient = new SubscriptionClient(this.auth0Client);
    this.catalogClient = new CatalogClient(this.auth0Client);
    this.invoiceClient = new InvoiceClient(this.auth0Client);
    this.receiptClient = new ReceiptClient(this.auth0Client);
    this.memberBillingClient = new MemberBillingClient(this.auth0Client);
    this.paymentMethodClient = new PaymentMethodClient(this.auth0Client);
    this.billingClient = new BillingClient(this.auth0Client);
    this.entitlementsApi = new EntitlementsApi(this.auth0Client);
  }

  // Entitlements
  async getEntitlements(orgId: string): Promise<Entitlement> {
    return this.entitlementsApi.getEntitlements(orgId);
  }

  // Subscription
  async getSubscription(orgId: string): Promise<Subscription | null> {
    // Get subscription and catalog in parallel
    const [subscription, catalog] = await Promise.all([
      this.subscriptionClient.getSubscription(orgId),
      this.catalogClient.getCatalog(),
    ]);

    if (!subscription) {
      return null;
    }

    // Find the tier information from the catalog
    const tierInfo = catalog.tiers.find(
      (tier) => tier.key === subscription.tier,
    );

    // Find the addon information from the catalog
    const addonsInfo = (subscription.addons || [])
      .map((addonKey: string) => {
        const addon = catalog.addons.find((a) => a.key === addonKey);
        return addon || null;
      })
      .filter((a: CatalogAddon | null): a is CatalogAddon => a !== null);

    // Return subscription enriched with tier and addon information
    return {
      ...subscription,
      tierInfo: tierInfo || null,
      addonsInfo,
      calculatedTotalCents: subscription.calculatedTotalCents,
      fixedPriceCents: subscription.fixedPriceCents,
    };
  }

  async updateSubscription(
    orgId: string,
    payload: Subscription,
  ): Promise<Subscription> {
    return this.subscriptionClient.updateSubscription(orgId, payload);
  }

  // Legacy methods for backward compatibility
  async updatePhysicians(
    orgId: string,
    change: "add" | "remove",
    count: number,
  ): Promise<UpdatePhysiciansResponse> {
    return this.subscriptionClient.updatePhysicians(orgId, change, count);
  }

  async createProvisionalBillingLedgerEntry(
    orgId: string,
    payload: Record<string, unknown>,
  ): Promise<unknown> {
    return this.subscriptionClient.createProvisionalBillingLedgerEntry(
      orgId,
      payload,
    );
  }

  async confirmProrationEvents(
    orgId: string,
    eventIds: string[],
  ): Promise<unknown> {
    return this.subscriptionClient.confirmProrationEvents(orgId, eventIds);
  }

  async cancelProrationEvents(
    orgId: string,
    eventIds: string[],
  ): Promise<void> {
    return this.subscriptionClient.cancelProrationEvents(orgId, eventIds);
  }

  async rollbackProrationEvents(
    orgId: string,
    eventIds: string[],
  ): Promise<void> {
    return this.subscriptionClient.rollbackProrationEvents(orgId, eventIds);
  }

  async getBillingLedgerEntries(
    orgId: string,
    options?: BillingLedgerQueryOptions,
  ): Promise<BillingLedgerEntry[]> {
    return this.subscriptionClient.getBillingLedgerEntries(orgId, options);
  }

  // Catalog
  async getCatalog(): Promise<CatalogResponse> {
    return this.catalogClient.getCatalog();
  }

  // Invoices
  async getInvoices(orgId: string): Promise<SubscriptionInvoice[]> {
    return this.invoiceClient.getInvoices(orgId);
  }

  async getInvoice(invoiceId: string): Promise<SubscriptionInvoice> {
    return this.invoiceClient.getInvoice(invoiceId);
  }

  async contestInvoice(
    invoiceId: string,
    reason: string,
    options?: { orgAuthId?: string; contestedBy?: string },
  ): Promise<SubscriptionInvoice> {
    return this.invoiceClient.contestInvoice(invoiceId, reason, options);
  }

  async getInvoiceByLedgerId(ledgerId: string): Promise<SubscriptionInvoice> {
    return this.invoiceClient.getInvoiceByLedgerId(ledgerId);
  }

  async getInvoicesByCycle(
    orgId: string,
    options?: { cycleMonth: number; cycleYear: number },
  ): Promise<SubscriptionInvoice[]> {
    return this.invoiceClient.getInvoicesByCycle(orgId, options);
  }

  async getBillingHistory(
    orgId: string,
    includeCancelled: boolean = true,
  ): Promise<unknown[]> {
    return this.invoiceClient.getBillingHistory(orgId, includeCancelled);
  }

  // Receipts
  async getReceipts(orgId: string): Promise<SubscriptionReceipt[]> {
    return this.receiptClient.getReceipts(orgId);
  }

  async getReceipt(receiptId: string): Promise<SubscriptionReceipt> {
    return this.receiptClient.getReceipt(receiptId);
  }

  async getReceiptByLedgerId(ledgerId: string): Promise<SubscriptionReceipt> {
    return this.receiptClient.getReceiptByLedgerId(ledgerId);
  }

  // Member billing
  async addMemberWithBilling(
    orgId: string,
    member: { name: string; email: string; userId?: string },
  ): Promise<unknown> {
    return this.memberBillingClient.addMember(orgId, member);
  }

  // Payment method
  async calculateSeatChangeImpact(
    orgId: string,
    deltaSeats: number,
    operation: "add" | "remove",
    roles?: string[],
    practitionerName?: string,
    practitionerId?: string,
  ): Promise<SeatChangeImpactResponseDto> {
    return this.subscriptionClient.calculateSeatChangeImpact(orgId, {
      deltaSeats,
      operation,
      roles,
      practitionerName,
      practitionerId,
    });
  }

  async calculateAddonChangeImpact(
    orgId: string,
    operation: "add" | "remove",
    addonKey: string,
  ): Promise<SeatChangeImpactResponseDto> {
    return this.subscriptionClient.calculateAddonChangeImpact(orgId, {
      operation,
      addonKey,
    });
  }

  async calculateInitialPeriodImpact(orgId: string): Promise<{
    willCharge: boolean;
    amountCents: number;
    currency: string;
    periodStart?: string;
    periodEnd?: string;
    description?: string;
    skipReason?: string;
  }> {
    return this.subscriptionClient.calculateInitialPeriodImpact(orgId);
  }

  async calculateTransitionImpact(
    orgId: string,
    payload: {
      targetTier: string;
      targetSeats: number;
      addons: string[];
      interval: string;
    },
  ): Promise<SeatChangeImpactResponseDto> {
    return this.subscriptionClient.calculateTransitionImpact(orgId, payload);
  }

  // Payment Method
  async getPaymentMethod(orgId: string): Promise<PaymentMethodResponseDto[]> {
    return this.paymentMethodClient.getPaymentMethod(orgId);
  }

  async getSetupIntent(orgId: string) {
    return this.paymentMethodClient.getSetupIntent(orgId);
  }

  // Billing (Administrative & Ledger)
  async disputeLedger(
    ledgerId: string,
    reason: string,
    options?: { orgAuthId?: string; disputedBy?: string },
  ): Promise<BillingLedgerEntry> {
    return this.billingClient.disputeLedger(ledgerId, reason, options);
  }

  async getBillingLedgerEntryById(
    ledgerId: string,
  ): Promise<BillingLedgerEntry> {
    return this.billingClient.getBillingLedgerEntryById(ledgerId);
  }

  async generateRecurringInvoices(
    orgIds?: string[],
  ): Promise<GenerateRecurringInvoicesResponse> {
    return this.billingClient.generateRecurringInvoices(orgIds);
  }

  async simulateDimepayWebhook(
    payload: SimulateDimepayWebhookRequest,
  ): Promise<unknown> {
    return this.billingClient.simulateDimepayWebhook(payload);
  }

  async simulateDelinquency(
    payload: SimulateDelinquencyRequest,
  ): Promise<unknown> {
    return this.billingClient.simulateDelinquency(payload);
  }

  async resetDelinquency(payload: { orgId: string }): Promise<unknown> {
    return this.billingClient.resetDelinquency(payload);
  }

  async applyLateFees(orgIds?: string[]): Promise<ApplyLateFeesResponse> {
    return this.billingClient.applyLateFees(orgIds);
  }

  async processDueInvoices(
    orgIds?: string[],
  ): Promise<ProcessDueInvoicesResponse> {
    return this.billingClient.processDueInvoices(orgIds);
  }

  async checkTrialExpirations(
    orgIds?: string[],
  ): Promise<CheckTrialExpirationsResponse> {
    return this.billingClient.checkTrialExpirations(orgIds);
  }

  async processPeriodEndCancellations(
    orgIds?: string[],
  ): Promise<ProcessPeriodEndJobResponse> {
    return this.billingClient.processPeriodEndCancellations(orgIds);
  }

  async processPeriodEndAddonRemovals(
    orgIds?: string[],
  ): Promise<ProcessPeriodEndJobResponse> {
    return this.billingClient.processPeriodEndAddonRemovals(orgIds);
  }

  async getAllBillingLedger(orgId: string): Promise<BillingLedgerEntry[]> {
    return this.billingClient.getAllBillingLedger(orgId);
  }

  async getBillingLedgerByCycle(
    orgId: string,
    startTime: string,
    endTime: string,
  ): Promise<BillingLedgerEntry[]> {
    return this.billingClient.getBillingLedgerByCycle(
      orgId,
      startTime,
      endTime,
    );
  }

  async payAllInvoices(
    orgId: string,
    paymentMethodId: string,
  ): Promise<BillingLedgerEntry[]> {
    return this.billingClient.payAllInvoices(orgId, paymentMethodId);
  }

  async paySelectedInvoices(
    orgId: string,
    ledgerIds: string[],
    paymentMethodId: string,
  ): Promise<BillingLedgerEntry[]> {
    return this.billingClient.paySelectedInvoices(
      orgId,
      ledgerIds,
      paymentMethodId,
    );
  }

  // Admin Subscription Management
  async updateSeatCount(
    orgId: string,
    newSeatCount: number,
    effectiveDate?: string,
  ): Promise<unknown> {
    return this.billingClient.updateSeatCount(orgId, {
      newSeatCount,
      effectiveDate,
    });
  }

  async changeTier(
    orgId: string,
    newTier: string,
    effectiveDate?: string,
    dryRun?: boolean,
  ): Promise<SeatChangeImpactResponseDto> {
    return this.subscriptionClient.changeTier(orgId, {
      newTier,
      effectiveDate,
      dryRun,
    });
  }

  async cancelSubscription(orgId: string): Promise<unknown> {
    return this.subscriptionClient.cancelSubscription(orgId);
  }

  async requestAddonRemoval(
    orgId: string,
    addonKey: string,
    payload: { reason: string; userEmail: string; userName: string },
  ): Promise<unknown> {
    return this.subscriptionClient.requestAddonRemoval(
      orgId,
      addonKey,
      payload,
    );
  }
}

export default SubscriptionApi;
